import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { X, Plus } from 'lucide-react';

const SendInvoiceDialog = ({ open, onOpenChange, onSend, invoiceNumber, customerEmail, defaultCcEmails = [] }) => {
  const { t } = useTranslation();
  const [ccEmails, setCcEmails] = useState(['']);
  const [loading, setLoading] = useState(false);

  // Load default CC emails when dialog opens
  React.useEffect(() => {
    if (open && defaultCcEmails && defaultCcEmails.length > 0) {
      setCcEmails(defaultCcEmails);
    } else if (open) {
      setCcEmails(['']);
    }
  }, [open, defaultCcEmails]);

  const handleAddEmail = () => {
    setCcEmails([...ccEmails, '']);
  };

  const handleRemoveEmail = (index) => {
    const newEmails = ccEmails.filter((_, i) => i !== index);
    setCcEmails(newEmails.length > 0 ? newEmails : ['']);
  };

  const handleEmailChange = (index, value) => {
    const newEmails = [...ccEmails];
    newEmails[index] = value;
    setCcEmails(newEmails);
  };

  const handleSend = async () => {
    setLoading(true);
    
    // Filter out empty email addresses
    const validEmails = ccEmails.filter(email => email.trim() !== '');
    
    try {
      await onSend(validEmails);
      // Reset on success
      setCcEmails(['']);
      onOpenChange(false);
    } catch (error) {
      // Error handling is done in parent component
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setCcEmails(['']);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="rounded-sm max-w-md">
        <DialogHeader>
          <DialogTitle className="font-heading">{t('sendDialog.title')}</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          <div className="bg-slate-50 p-3 rounded-sm border border-slate-200">
            <p className="text-xs text-slate-500 mb-1">{t('sendDialog.invoice')}</p>
            <p className="font-mono text-sm font-bold text-slate-900">#{invoiceNumber}</p>
            
            <p className="text-xs text-slate-500 mt-3 mb-1">{t('sendDialog.mainRecipient')}</p>
            <p className="text-sm text-slate-900">{customerEmail}</p>
          </div>

          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label className="text-sm font-medium">{t('sendDialog.ccRecipients')}</Label>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={handleAddEmail}
                className="h-7 text-xs rounded-sm"
              >
                <Plus className="w-3 h-3 mr-1" />
                {t('sendDialog.addEmail')}
              </Button>
            </div>
            
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {ccEmails.map((email, index) => (
                <div key={index} className="flex gap-2">
                  <Input
                    type="email"
                    placeholder={t('sendDialog.emailPlaceholder')}
                    value={email}
                    onChange={(e) => handleEmailChange(index, e.target.value)}
                    className="rounded-sm text-sm"
                  />
                  {ccEmails.length > 1 && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => handleRemoveEmail(index)}
                      className="h-9 w-9 rounded-sm flex-shrink-0"
                    >
                      <X className="w-4 h-4 text-slate-600" />
                    </Button>
                  )}
                </div>
              ))}
            </div>
            
            <p className="text-xs text-slate-500">
              {t('sendDialog.ccHint')}
            </p>
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={handleClose}
            disabled={loading}
            className="rounded-sm"
          >
            {t('common.cancel')}
          </Button>
          <Button
            type="button"
            onClick={handleSend}
            disabled={loading}
            className="rounded-sm bg-primary hover:bg-[#D62839]"
          >
            {loading ? t('sendDialog.sending') : t('sendDialog.send')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SendInvoiceDialog;
